export default {
	/**
	 * Update the recommendations that are dismissed in state and the cookie
	 *
	 * Gets the current dismissed recommendations from the cookie and saves it.
	 * If a new slug is being passed in, add the new value to both the state and the cookie.
	 *
	 * @param {state} state The current state
	 * @param {string} newSlug A new slug/ID to add to the list.
	 *
	 * @returns {void}
	 */
	setDismissedRecommendations(state, newSlug = '') {
		const cookie = this._vm.$cookies.get('omRecommendationsDismissed');
		const dismissedSlugs = cookie ? JSON.parse(cookie) : [];

		if (newSlug) {
			dismissedSlugs.push(newSlug);
			this._vm.$cookies.create('omRecommendationsDismissed', JSON.stringify(dismissedSlugs));
		}

		this._vm.$set(state, 'dismissedRecommendations', dismissedSlugs);
	},
	/**
	 * Updates state with the new search term
	 *
	 * @param {state} state The current state
	 * @param {string} payload The new search string
	 *
	 * @returns {void}
	 */
	setSearch(state, payload) {
		state.search = payload;
	},
	/**
	 * Updates state with the new search term
	 *
	 * @param {state} state The current state
	 * @param {string} payload The new sort string
	 *
	 * @returns {void}
	 */
	setSort(state, payload) {
		state.sort = payload;
	},
	/**
	 * Updates state with the filter options available
	 *
	 * @param {state} state The current state
	 * @param {string} payload The filter options array
	 *
	 * @returns {void}
	 */
	setFilterOptions(state, payload) {
		this._vm.$set(state, 'filterOptions', payload);
	},
	/**
	 * Clears all filters from the state
	 *
	 * @param {state} state The current state
	 *
	 * @returns {void}
	 */
	clearFilters(state) {
		this._vm.$set(state.filters, 'goals', []);
		this._vm.$set(state.filters, 'categories', []);
		this._vm.$set(state.filters, 'tags', []);
	},
	/**
	 * Updates state with the new filter term and value
	 *
	 * @param {state} state The current state
	 * @param {string} payload The new filter
	 *
	 * @returns {void}
	 */
	setFilter(state, payload) {
		this._vm.$set(state.filters, payload.filter, payload.value);
	},
	/**
	 * Updates state with the rule data
	 *
	 * @param {state} state The current state
	 * @param {Array} payload An array of rules
	 *
	 * @returns {void}
	 */
	setRules(state, payload) {
		this._vm.$set(state, 'rules', payload);
	},
	/**
	 * Updates state with the playbooks available
	 *
	 * @param {state} state The current state
	 * @param {Array} payload An array of Playbooks
	 *
	 * @returns {void}
	 */
	setPlaybooks(state, payload) {
		this._vm.$set(state, 'playbooks', payload);
	},
	/**
	 * Updates state with the selected playbook
	 *
	 * @param {state} state The current state
	 * @param {string} payload The selected Playbook
	 *
	 * @returns {void}
	 */
	setSelectedPlaybook(state, payload) {
		this._vm.$set(state, 'selectedPlaybook', payload);
	},
	/**
	 * Update state with the rule the user needs to upgrade in order to use
	 *
	 * @param {state} state The current state
	 * @param {string} payload The rule the user doesn't have access to.
	 *
	 * @returns {void}
	 */
	setUpgradeRule(state, payload) {
		this._vm.$set(state, 'upgradeRule', payload);
	},
	/**
	 * Save the recommendations from the app to state
	 *
	 * Uses the recommendations list from the app and the playbooks data we have stored
	 * to generate a list of recommendations that the user can see.
	 *
	 * @param {state} state The current state
	 * @param {Array} payload An array of Recommendations
	 *
	 * @returns {void}
	 */
	setRecommendations(state, payload) {
		let playbooks = state.playbooks;

		// I'm using reduce so I can remove any recommendations where we aren't able to find a matching playbook
		// while mutating the array to fit the format we need (i.e. adding type, getting playbooks objects, etc).
		let recommendations = payload.reduce((recommendations, r) => {
			// I'm using reduce so I can simultaneously check if we have the playbook for the given slug
			// and if it does, add it.
			let playbookList = r.playbooks.reduce((playbookList, p) => {
				const playbook = playbooks.find((pb) => pb.id === p);
				if (playbook) {
					playbookList.push(playbook);
				}
				return playbookList;
			}, []);

			if (0 < playbookList.length) {
				r.cardType = 'recommendation';
				r.playbooks = playbookList;
				recommendations.push(r);
			}

			return recommendations;
		}, []);

		this._vm.$set(state, 'recommendations', recommendations);
	},
};
